import { DataKeys } from '@constants/login';
import { DEFAULT_SUBFLOW } from '@conversed/shared/src/central/constants/flow';
import { IS_MOBILE } from '@hooks/useMedia';
import { EFlowDirection } from '@interfaces/EFlowDirection';
import type { TLoginInner } from '@interfaces/redux/TLoginState';
import type { TReduxState } from '@interfaces/TReduxState';

// TODO make abstraction for local storage
const getInitialAccessToken = () =>
  localStorage.getItem(DataKeys.ACCESS_TOKEN_DATA_KEY) || undefined;
const getInitialRefreshToken = () =>
  localStorage.getItem(DataKeys.REFRESH_TOKEN_DATA_KEY) || undefined;
const getInitialLoggedIn = () =>
  !!getInitialAccessToken() && !!getInitialRefreshToken();
// Mobile starts with the sidebar hidden (it overlays content when open);
// desktop remembers the user's last choice and defaults to expanded.
const sidebarOpenInitialState = IS_MOBILE
  ? false
  : localStorage.getItem(DataKeys.SIDEBAR_OPEN) !== 'false';

export const getInitialLoginCredentials = (): TLoginInner => ({
  isLoggedIn: getInitialLoggedIn(),
  refreshToken: getInitialRefreshToken(),
  accessToken: getInitialAccessToken(),
});

export const userDefaults = {
  currentUser: undefined,
  email: '',
  password: '',
  permissions: undefined,
  mfaData: {
    qrCode: '',
    name: '',
    key: '',
  },
};

export const initialState: TReduxState = {
  user: userDefaults,
  ui: {
    profileDropdownOpen: false,
    sideBarOpen: sidebarOpenInitialState,
    isLoading: false,
    loadingStates: [],
    activeModal: undefined,
  },
  login: getInitialLoginCredentials(),
  chat: {
    selectedConversation: undefined,
    typingIndicator: false,
    chatInputMessage: '',
    cannedResponses: [],
    cannedResponsesIndex: 0,
    isCannedResponsesMenuOpen: false,
    chatList: [],
    inboxes: undefined,
    notifications: [],
  },
  settings: {
    agents: [],
    selectedAgent: undefined,
    inboxes: [],
    selectedInbox: undefined,
    selectedCannedResponse: undefined,
    labels: [],
    selectedLabel: undefined,
    webclient: {
      botId: '',
      configs: [],
      isEnabledWebclientAccess: true,
      isLoadingConfigs: true,
    },
    facebookPages: [],
    roles: [],
    links: [],
    exportConfigs: [],
    debugConfig: undefined,
    selectedExportConfig: undefined,
    validationConfig: undefined,
    handover: {
      availabilities: [],
      selectedDate: undefined,
      filter: 'all',
      calendarView: 'week',
    },
    userDataMapping: {
      email: '',
      phone: '',
      address: '',
      name: '',
    },
    timezone: '',
    useStreamingResponse: false,
    messengerSettings: {},
  },
  designer: {
    selectedBlock: undefined,
    targetBlock: undefined,
    blocksSearchString: '',
    blocksSearchMatchIndex: 0,
    flowDirection: EFlowDirection.Vertical,
    menuEntries: [],
    blocks: undefined,
    blockUpdates: {
      created: [],
      updated: [],
      deleted: [],
      subFlowsCreated: [],
      subFlowsDeleted: [],
    },
    blockLanguage: '',
    blockLanguages: [],
    flow: {
      selectedSubFlow: DEFAULT_SUBFLOW,
      subFlows: [DEFAULT_SUBFLOW],
    },
    revisions: [],
    subflowTemplates: {
      templates: [],
      selectedTemplate: undefined,
      search: '',
    },
  },
  activityLog: {
    logs: [],
  },
  gdpr: {
    gdprUsers: [],
    totalRecords: 0,
    selectedUser: undefined,
  },
  exports: { files: [], selectedFile: undefined, search: '' },
  colors: {
    default: '#aaaaaa',
  },
  preview: {
    backgroundImage: '',
  },
  dataSources: {
    files: [],
    search: '',
    lastTrainingActionAt: null,
  },
  metrics: {
    // * 7 days in the past
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
    endDate: new Date(),
    data: {
      totalLogins: 0,
      averageLogins: 0,
      peakDay: 0,
      companyTrainAIData: [],
      trainAIMetrics: {
        total: 0,
        average: 0,
        best: {
          name: '',
          totalNumber: null,
        },
      },
      totalUserOver30Days: 0,
      newUsersOver30Days: 0,
      inactiveUsersOver30Days: 0,
    },
  },
  templateResponses: {
    templates: [],
    selectedTemplate: undefined,
  },
  voiceAi: {
    agents: [],
    selectedAgentId: undefined,
    reason: undefined,
  },
  conversationKeywords: {
    keywords: [],
    selectedKeyword: undefined,
  },
};
